import { QRCodeSVG } from 'qrcode.react';
import { useEffect } from 'react';
import styled from 'styled-components';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { Modal } from '@/components/uielements/modal';
import { Stack } from '@/components/uielements/stack/stack';
import { useDispatch, useSelector } from '@/redux';
import meActions from '@/redux/me/actions';
import { meSliceSelector } from '@/redux/me/reducer';
import { dateFormatter } from '@/utils/loyalty';

interface Props {
  id?: number | string;
  type?: string;
  memberCode?: string | null;
  memberId?: string | null;
  onError?: () => void;
}

export const ScanCode = ({ id, type, memberCode, memberId, onError = undefined }: Props) => {
  const { activeRedemption, loading, error } = useSelector(meSliceSelector);
  const { organization } = useSelector((state) => state.app.organization);
  const dispatch = useDispatch();

  const QRCodeValue = activeRedemption ? activeRedemption.tracking_code : memberCode;
  const label = memberCode ? 'Member Code' : activeRedemption?.label;

  useEffect(() => {
    if (id && type) {
      dispatch(meActions.createRedemption(id, type));
    }
  }, []);

  useEffect(() => {
    // @ts-expect-error
    error && onError();
  }, [error]);

  return (
    <>
      <Modal.Title>
        <StringAccessor
          accessor={
            memberCode
              ? 'rewards.earn_in_store_modal_header'
              : 'rewards.redeem_in_store_modal_header'
          }
          html={true}
          dataObj={{ brand: organization.label }}
        />
      </Modal.Title>

      <Modal.Content>
        <Stack
          align="center"
          direction="vertical"
          gap="var(--size-4)"
          overrides={{
            minHeight: '100%',
            textAlign: 'center',
          }}
        >
          {loading && id && type ? (
            <Loading>loading...</Loading>
          ) : (
            <>
              <div>
                {label && <Title>{label}</Title>}

                {activeRedemption?.expiring_at && (
                  <Expires>Expires: {dateFormatter(activeRedemption.expiring_at)}</Expires>
                )}
              </div>

              {QRCodeValue && <QRCodeSVG value={QRCodeValue} size={160} />}

              {activeRedemption?.tracking_code && (
                <span>Tracking Code: {activeRedemption.tracking_code}</span>
              )}

              {memberId && <span>Member Id: {memberId}</span>}

              <Disclaimer>
                <StringAccessor
                  accessor={
                    memberCode
                      ? 'rewards.earn_in_store_instructional_text'
                      : 'rewards.redeem_in_store_instructional_text'
                  }
                  html={true}
                />
              </Disclaimer>
            </>
          )}
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Cancel>Close</Modal.Cancel>
      </Modal.Footer>
    </>
  );
};

const Title = styled.h2`
  margin: 0;
`;

const Expires = styled.span`
  display: block;
  padding-block-start: var(--size-2);
  opacity: 0.75;
`;

const Disclaimer = styled.div`
  font-size: 0.675rem;
`;

const Loading = styled.span`
  margin: auto 0;
`;
